import React, {useEffect} from 'react';
import {withRouter} from "react-router";
import {Translation, getI18n} from 'react-i18next';
import {Button, Table} from 'antd';
import {EyeOutlined} from '@ant-design/icons';

import ResourceTag from './ResourceTag';
import ProgressTag from './ProgressTag';
import {getColumnSearchProps, getColumnDateRangeProps} from '../../../elements/components/TableColumnFilters';
import {StatusTag} from '../../../elements/components/Tags';
import {pathTo} from '../../../Routes';
import Format from '../../../../../lib/Format';
import QueryString from '../../../../../lib/QueryString';
import Logger from '../../../../../lib/Logger';

const JobsList = ({component, page, limit, order, filter, total, load, remove, history, ...props}) => {

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    delete filter[dataIndex];
    history.push(QueryString.append(props.location.pathname, {order, ...filter}));
  };

  const columns = [
    {
      title: getI18n().t('table_header_id'),
      dataIndex: 'id',
      key: 'id',
      defaultSortOrder: order === 'id.asc' ? 'ascend' : (order === 'id.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnSearchProps('id', handleSearch, handleReset, getI18n().t('table_header_id'), filter),
    },
    {
      title: getI18n().t('imports_jobs_resource'),
      dataIndex: 'resource',
      key: 'resource',
      filters: [
        {text: getI18n().t('imports_jobs_resource_resource'), value: 1},
      ],
      filteredValue: 'resource' in filter ? filter['resource'].split(',') : null,
      render: code => <ResourceTag resource={code} />,
    },
    {
      title: getI18n().t('imports_jobs_upload_date'),
      dataIndex: 'upload_date',
      key: 'upload_date',
      render: date => Format.date(date),
      defaultSortOrder: order === 'upload_date.asc' ? 'ascend' : (order === 'upload_date.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('upload_date', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('imports_jobs_complete_date'),
      dataIndex: 'complete_date',
      key: 'complete_date',
      render: date => Format.date(date),
      defaultSortOrder: order === 'complete_date.asc' ? 'ascend' : (order === 'complete_date.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('complete_date', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('imports_jobs_progress'),
      dataIndex: 'progress',
      key: 'progress',
      filters: [
        {text: getI18n().t('imports_jobs_progress_queued'), value: 1},
        {text: getI18n().t('imports_jobs_progress_processing'), value: 2},
        {text: getI18n().t('imports_jobs_progress_complete'), value: 3},
        {text: getI18n().t('imports_jobs_progress_failed'), value: 4},
      ],
      filteredValue: 'progress' in filter ? filter['progress'].split(',') : null,
      render: code => <ProgressTag progress={code} />,
    },
    {
      title: getI18n().t('imports_jobs_records_processed'),
      dataIndex: 'records_processed',
      key: 'records_processed',
    },
    {
      title: getI18n().t('imports_jobs_records_failed'),
      dataIndex: 'records_failed',
      key: 'records_failed',
    },
    {
      title: getI18n().t('table_header_status'),
      dataIndex: 'status',
      key: 'status',
      filters: [
        {text: getI18n().t('status_enabled'), value: 1},
        {text: getI18n().t('status_disabled'), value: 2},
        {text: getI18n().t('status_archived'), value: 3},
        {text: getI18n().t('status_deleted'), value: 4},
        {text: getI18n().t('status_pending'), value: 5},
      ],
      filteredValue: 'status' in filter ? filter['status'].split(',') : null,
      render: code => <StatusTag status={code} />,
    },
    {
      title: getI18n().t('table_header_updated_at'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: date => Format.date(date),
      defaultSortOrder: order === 'updated_at.asc' ? 'ascend' : (order === 'updated_at.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('updated_at', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('table_header_created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: date => Format.date(date),
      defaultSortOrder: order === 'created_at.asc' ? 'ascend' : (order === 'created_at.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('created_at', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('table_header_actions'),
      dataIndex: 'id',
      key: 'actions',
      render: id =>
        <>
          <Button
            icon={<EyeOutlined />}
            onClick={e => history.push(pathTo('JobViewScreen', {id: id}))}
          />
        </>
    },
  ];

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  }

  const handleTableChange = (pagination, filters, sorter) => {

    let path = props.location.pathname;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, {page: pagination['current']});
    }

    // handle sorting
    if ('field' in sorter && 'order' in sorter) {
      if (sorter['field'] && sorter['order']) {
        const order = sorter['field'] + '.' + (sorter['order'] === 'ascend' ? 'asc' : 'desc');
        params['order'] = order;
      }
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    history.push(QueryString.append(path, params));
  }

  const filterString = JSON.stringify(filter);
  useEffect(() => {
    load(page, limit, order, JSON.parse(filterString));
  }, [page, limit, order, filterString, load]);

  return (
    <Translation>{(t) => 

      <Table
        dataSource={props.list}
        columns={columns}
        pagination={page === 1 && total <= limit  ? false : pagination}
        loading={props.isLoading}
        scroll={{x: true}}
        onChange={handleTableChange}
      />

    }</Translation>
  );
}

export default withRouter(JobsList);

Logger.log('silly', `JobsList loaded.`);
